// Implements: F-UIX-018, F-OBS-011
/**
 * The audit trail of one organization (UIX.7): when, who, what, and on which team and mockup.
 * Pure presentation.
 *
 * **No id is ever shown.** The actor is a name, "Persona eliminada" for an erased person (whatever
 * the server wrote), "Sistema" for a revocation no person made, a link's visitor for a wrong share
 * password; a team and a mockup are named from the lists the caller passes, or read as not
 * available. A mockup reads by the caller's list, which holds the names the trail itself carried
 * (trashed and never-generated ones included, a trashed one marked "(en la papelera)"), else as not
 * available.
 * There is no IP, address, message or content column: the server sends none.
 * Rows are keyed by their position in the merged list (rows carry no id of their own).
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { AUDIT_ACTIONS } from "../../../types/audit";
import type { AuditAction, AuditRowView } from "../../../types/audit";
import { actorOf, instantText } from "../model/audit";
import type { NamedMockup } from "../model/audit";
import styles from "./Audit.module.css";
import { ACTION_LABEL_KEYS } from "./auditActionLabels";
import tableStyles from "./MyUsage.module.css";

type Translate = ReturnType<typeof useTranslation>["t"];

const KNOWN_ACTIONS: ReadonlySet<string> = new Set(AUDIT_ACTIONS);

function isKnown(action: string): action is AuditAction {
  return KNOWN_ACTIONS.has(action);
}

/** An action's label; one a newer server added reads as "another action", never as its token. */
function actionText(action: string, t: Translate): string {
  return isKnown(action) ? t(ACTION_LABEL_KEYS[action]) : t("backoffice.audit.action.unknown");
}

function MockupCell({ row, known }: { readonly row: AuditRowView; readonly known: ReadonlyMap<string, NamedMockup> }): ReactElement {
  const { t } = useTranslation();
  if (row.mockup_id === null) return <>{t("backoffice.audit.mockup.none")}</>;
  const mockup = known.get(row.mockup_id);
  if (mockup === undefined) return <>{t("backoffice.audit.mockup.unnamed")}</>;
  return mockup.trashed ? (
    <>
      {mockup.name} <span className={styles.trashed}>{t("backoffice.audit.mockup.trashed")}</span>
    </>
  ) : (
    <>{mockup.name}</>
  );
}

function actorText(row: AuditRowView, t: Translate): string {
  const actor = actorOf(row);
  return actor.kind === "name" ? actor.name : t(`backoffice.audit.actor.${actor.kind}`);
}

export interface AuditTableProps {
  /** The table's accessible name. */
  readonly labelledBy: string;
  readonly rows: readonly AuditRowView[];
  readonly teamNames: ReadonlyMap<string, string>;
  /** The mockups the caller can name: those the trail named and the per-mockup report's. */
  readonly mockupNames: ReadonlyMap<string, NamedMockup>;
}

export function AuditTable({ labelledBy, rows, teamNames, mockupNames }: AuditTableProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className={styles.scroll}>
      <table className={tableStyles.table} aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th scope="col">{t("backoffice.audit.column.when")}</th>
            <th scope="col">{t("backoffice.audit.column.actor")}</th>
            <th scope="col">{t("backoffice.audit.column.action")}</th>
            <th scope="col">{t("backoffice.audit.column.team")}</th>
            <th scope="col">{t("backoffice.audit.column.mockup")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // A row has no id; the list only ever grows at its end, so a position names one row.
            <tr key={index}>
              <td>
                <time dateTime={row.occurred_at}>{instantText(row.occurred_at)}</time>
              </td>
              <td>{actorText(row, t)}</td>
              <td>{actionText(row.action, t)}</td>
              <td>
                {row.team_id === null
                  ? t("backoffice.consumption.subject.noTeam")
                  : (teamNames.get(row.team_id) ?? t("backoffice.consumption.subject.unknownTeam"))}
              </td>
              <td>
                <MockupCell row={row} known={mockupNames} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
